import produce from "immer";
import { nanoid } from "nanoid";

import {
  ADD_EMPTY_SUB_KIT,
  ADD_KIT_DIMENSION,
  ADD_KIT_LENGTH,
  ADD_KIT_LINEAL_FEET_DIMENSION,
  ADD_KIT_SQUARE_FEET_DIMENSION,
  ADD_LABOUR_ITEM_TO_SUBKIT,
  ADD_LABOUR_ITEMS,
  ADD_NOTE,
  ADD_NOTE_TO_SUBKIT,
  ADD_PRODUCT_TO_CART,
  ADD_PRODUCT_TO_SUB_KIT_CART,
  ADD_SUB_KIT_DIMENSION,
  ADD_SUB_KIT_FROM_KIT,
  ADD_SUB_KIT_LENGTH,
  ADD_SUB_KIT_LINEAL_FEET_DIMENSION,
  ADD_SUB_KIT_SQUARE_FEET_DIMENSION,
  CREATE_PRODUCT_KIT_FROM_CART,
  CREATE_PRODUCT_KIT_FROM_LABOUR_LINE_ITEMS_CART,
  CREATE_SUB_KIT_FROM_LABOUR_LINE_ITEMS_CART,
  DELETE_CART_ATTRIBUTES,
  DELETE_CART_ITEM,
  DELETE_KIT_DIMENSION,
  DELETE_KIT_LENGTH,
  DELETE_KIT_LINEAL_FEET_DIMENSION,
  DELETE_KIT_SQUARE_FEET_DIMENSION,
  DELETE_SUB_KIT,
  DELETE_SUB_KIT_CART_ATTRIBUTES,
  DELETE_SUB_KIT_CART_ITEM,
  DELETE_SUB_KIT_DIMENSION,
  DELETE_SUB_KIT_LENGTH,
  DELETE_SUB_KIT_LINEAL_FEET_DIMENSION,
  DELETE_SUB_KIT_SQUARE_FEET_DIMENSION,
  EDIT_SELECTED_ATTRIBUTE_VISIBLITY,
  REORDER_KIT_PRODUCT_ROWS,
  REORDER_KIT_PRODUCTS,
  REORDER_SUB_KIT_PRODUCT_ROWS,
  REORDER_SUB_KIT_PRODUCTS,
  REORDER_SUB_KITS,
  RESET,
  RESET_EXPANDED_PRODUCTS,
  RESET_EXPANDED_SUB_KIT_PRODUCTS,
  RESET_PRODUCT_KIT,
  SET_CATEGORIES,
  SET_EMPTY_PRODUCTS,
  SET_ERROR_IN_CUSTOM_FORMULA,
  SET_ERROR_IN_SUB_KIT_CUSTOM_FORMULA,
  SET_EXPANDED_CATEGORIES,
  SET_EXPANDED_PRODUCTS,
  SET_EXPANDED_SUB_KIT_PRODUCTS,
  SET_IS_CALCULATING_PRODUCT_KIT_COST,
  SET_IS_FETCHING_CATEGORIES,
  SET_IS_FETCHING_PRODUCT_KIT,
  SET_IS_FETCHING_PRODUCT_KIT_MODAL,
  SET_IS_FETCHING_PRODUCTS,
  SET_IS_SAVING,
  SET_IS_SUB_KIT_MODAL_OPEN,
  SET_KIT_LABOUR_LINE_ITEMS,
  SET_PRODUCT_KIT,
  SET_PRODUCTS,
  SET_SEARCH,
  SET_SELECTED_PRICE_LEVELS,
  SET_TARGET_SUB_KIT_INDEX,
  TOGGLE_CART,
  TOGGLE_PRODUCT_KIT_MODAL,
  UPDATE_CART,
  UPDATE_CART_ATTRIBUTES,
  UPDATE_CART_COLOUR,
  UPDATE_CART_PRICE,
  UPDATE_CART_QUANTITY,
  UPDATE_CURRENT_PRODUCT_KIT,
  UPDATE_LABOUR_ITEM,
  UPDATE_LABOUR_ITEM_IN_SUBKIT,
  UPDATE_NOTE,
  UPDATE_NOTE_IN_SUBKIT,
  UPDATE_PRODUCT_KIT_PRICING_TABLE,
  UPDATE_SUB_KIT_CART,
  UPDATE_SUB_KIT_CART_ATTRIBUTES,
  UPDATE_SUB_KIT_CART_COLOUR,
  UPDATE_SUB_KIT_CART_PRICE,
  UPDATE_SUB_KIT_CART_QUANTITY,
  UPDATE_SUB_KIT_TITLE,
} from "@/redux/productKits/productKits.actionTypes";
import {
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
  PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
} from "@/utils/constants";
import {
  getLinealFeetQuantityInMm,
  getLinealMetresQuantityInMm,
  getSquareFeetQuantityInSquareMeter,
  getSquareMetresQuantityInSquareMetres,
} from "@/utils/measurement/measurement-for-price-computation";

import setKeypathValue from "../utils/setKeypathValue";

const buildProductKitProducts = (kitProducts) => {
  const result = {};
  kitProducts.forEach((cartItem, cartIndex) => {
    const {
      product,
      productType,
      productRow,
      quantity,
      customAttributeValues,
      customRowPrices,
      customFormula,
      errorInCustomFormula,
      lengthItems,
      sumLengths,
    } = cartItem;

    const newProductRow = {
      ...productRow,
      quantity,
      cartIndex,
      customAttributeValues,
      customRowPrices,
      customFormula,
      errorInCustomFormula,
      lengthItems,
      sumLengths,
    };

    if (cartItem.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
      const productRows = result[product.uid]
        ? [...result[product.uid].productRows, newProductRow]
        : [newProductRow];

      result[product.uid] = {
        ...result[product.uid],
        ...product,
        productType,
        suppliers: [],
        productRows,
      };
    }

    if (cartItem.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
      const noteItem = {
        uid: cartItem.uid,
        productType: cartItem.productType,
        notesType: cartItem.notesType,
        notes: cartItem.notes,
        cartIndex,
      };

      const noteItems = result.Notes
        ? [...result.Notes.noteItems, { ...noteItem }]
        : [{ ...noteItem }];

      result.Notes = {
        ...result.Notes,
        uid: PRODUCT_TYPE_NOTES_ITEM_LABEL,
        name: PRODUCT_TYPE_NOTES_ITEM_LABEL,
        productType,
        noteItems,
      };
    }

    if (cartItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
      const labourLineItem = {
        uid: cartItem.uid,
        quantity: parseFloat(cartItem.quantity),
        productType: cartItem.productType,
        hourlyRateCost: cartItem.customHourlyRateCost || cartItem.hourlyRateCost,
        hourlyRateCharged: cartItem.customHourlyRateCharged || cartItem.hourlyRateCharged,
        user: cartItem.user,
        firstName: cartItem.firstName,
        lastName: cartItem.lastName,
        cartIndex,
      };

      if (!labourLineItem.uid) {
        delete labourLineItem.uid;
      }

      const productRows = result.Labour
        ? [...result.Labour.productRows, { ...labourLineItem }]
        : [{ ...labourLineItem }];

      result.Labour = {
        ...result.Labour,
        uid: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
        name: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
        productType,
        productRows,
      };
    }
  });
  return result;
};

const initialState = {
  kitProducts: [],
  kitLabourLineItems: [],
  productKitProducts: {},
  selectedAttributeVisiblity: [
    {
      name: "Description",
      isHideOrderSummary: false,
      isHideInvoicePdf: false,
      isHideCustomerView: false,
      isHideWorkOrderPdf: false,
      uid: nanoid(),
    },
  ],
  currentProductKit: {
    image: "",
    title: "",
    description: "",
    customPricing: false,
    isMarginTrackedByCompany: false,
    categoryUid: null,
    category: null,
    displayKitItems: [
      "displayOnAllViews",
      "displayOnWorkOrderPdf",
      "displayOnDeliveryDocketPdf",
      "displayOnInvoicePdf",
      "displayOnQuotePdf",
      "displayOnProformaInvoicePdf",
      "displayOnCustomerOnlineView",
      "displayOnAccountingPackage",
    ],
    displaySubKits: [
      "displayOnAllViews",
      "displayOnWorkOrderPdf",
      "displayOnDeliveryDocketPdf",
      "displayOnInvoicePdf",
      "displayOnQuotePdf",
      "displayOnProformaInvoicePdf",
      "displayOnCustomerOnlineView",
      "displayOnAccountingPackage",
    ],
    productKitRows: [
      {
        attributes: [
          {
            name: "Description",
            value: "",
            isHideOrderSummary: false,
            isHideInvoicePdf: false,
            isHideCustomerView: false,
            isHideWorkOrderPdf: false,
          },
        ],
        rowPriceLevels: [],
        accountingItem: null,
      },
    ],
    fields: [
      {
        name: "Description",
        isHideOrderSummary: false,
        isHideInvoicePdf: false,
        isHideCustomerView: false,
        isHideWorkOrderPdf: false,
      },
    ],
    priceLevels: [],
    hiddenPriceLevels: [],
  },
  isModalVisible: false,
  isCartVisible: false,
  isFetchingCategories: false,
  isFetchingProductKit: false,
  isFetchingProductKitModal: false,
  isCalculatingProductKitCost: false,
  fetchingProductKitUid: null,
  isSaving: false,
  categories: [],
  expandedCategories: [],
  expandedProducts: [],
  expandedSubKitProducts: {},
  products: {},
  search: "",
  selectedPriceLevels: [],
  subKits: [],
  isSubKitModalOpen: false,
  subKitIndex: null,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case SET_SEARCH: {
      return {
        ...state,
        search: action.payload,
      };
    }

    case SET_IS_FETCHING_CATEGORIES: {
      return {
        ...state,
        isFetchingCategories: action.payload,
      };
    }

    case SET_IS_FETCHING_PRODUCT_KIT: {
      return {
        ...state,
        isFetchingProductKit: action.payload,
      };
    }

    case SET_IS_FETCHING_PRODUCT_KIT_MODAL: {
      return {
        ...state,
        isFetchingProductKitModal: action.payload.status,
        fetchingProductKitUid: action.payload.uid,
      };
    }

    case SET_IS_CALCULATING_PRODUCT_KIT_COST: {
      return {
        ...state,
        isCalculatingProductKitCost: action.payload,
      };
    }

    case SET_IS_SAVING: {
      return {
        ...state,
        isSaving: action.payload,
      };
    }

    case SET_CATEGORIES: {
      const { data } = action.payload;

      return {
        ...state,
        categories: data,
      };
    }

    case SET_EXPANDED_CATEGORIES: {
      const { data } = action.payload;

      return {
        ...state,
        expandedCategories: data,
      };
    }

    case SET_EXPANDED_PRODUCTS: {
      const { data } = action.payload;

      return {
        ...state,
        expandedProducts: data,
      };
    }

    case SET_EXPANDED_SUB_KIT_PRODUCTS: {
      const { subKitIndex, data } = action.payload;

      return {
        ...state,
        expandedSubKitProducts: {
          ...state.expandedSubKitProducts,
          [subKitIndex]: data,
        },
      };
    }

    case SET_IS_FETCHING_PRODUCTS: {
      const { data } = action.payload;
      return {
        ...state,
        categories: state.categories.map((c) =>
          c.uid === data.uid ? { ...c, isFetching: data.isFetching } : { ...c }
        ),
      };
    }

    case SET_PRODUCTS: {
      const { data, categoryUid } = action.payload;

      return {
        ...state,
        products: {
          ...state.products,
          [categoryUid]: data,
        },
      };
    }

    case SET_EMPTY_PRODUCTS: {
      return {
        ...state,
        products: {},
      };
    }

    case SET_ERROR_IN_CUSTOM_FORMULA: {
      const { cartIndex, errorInCustomFormula } = action.payload;
      const newCartItem = {
        ...state.kitProducts[cartIndex],
        errorInCustomFormula,
      };

      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex] = newCartItem;
      });
    }

    case SET_ERROR_IN_SUB_KIT_CUSTOM_FORMULA: {
      const { subKitIndex, cartIndex, errorInCustomFormula } = action.payload;
      const newCartItem = {
        ...state.subKits[subKitIndex].kitProducts[cartIndex],
        errorInCustomFormula,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex] = newCartItem;
      });
    }

    case ADD_PRODUCT_TO_CART: {
      const { product, productRow } = action.payload;
      const newProduct = {
        ...product,
        suppliers: [],
      };
      const newProductRow = {
        ...productRow.original,
      };
      const newCustomFormula = {
        ...product.customFormula,
        uid: null,
        kitProductUid: null,
        additionalProductUid: null,
      };
      const customRowPrices = newProductRow.rowPrices.map((rowPrice) => ({
        ...rowPrice,
        uid: null,
        value: 0,
      }));
      const newCartItem = {
        product: newProduct,
        productRow: newProductRow,
        productType: PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
        quantity: product.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL ? null : 1.0,
        actualQuantity:
          product.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL ? null : 1.0,
        isCompleted: false,
        lengthItems: [],
        actualLengthItems: [],
        sumLengths: [],
        actualSumLengths: [],
        discount: 0.0,
        customValue: null,
        totalValue: 0.0,
        types: newProductRow.types,
        productName: newProduct.name,
        customFormula:
          product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL
            ? newCustomFormula
            : null,
        errorInCustomFormula: "",
        uid: null,
        customAttributeValues: [],
        customRowPrices,
        cartIndex: state.kitProducts.length,
      };

      return {
        ...state,
        kitProducts: [...state.kitProducts, newCartItem],
      };
    }

    case ADD_LABOUR_ITEMS: {
      const data = action.payload;
      return {
        ...state,
        kitProducts: data,
      };
    }

    case UPDATE_LABOUR_ITEM: {
      const { labourItemIndex, data } = action.payload;

      const newCartItem = {
        ...state.kitProducts[labourItemIndex],
        ...data,
      };

      return produce(state, (draftState) => {
        draftState.kitProducts[labourItemIndex] = newCartItem;
      });
    }

    case UPDATE_LABOUR_ITEM_IN_SUBKIT: {
      const { subKitIndex, labourItemIndex, data } = action.payload;

      const item = {
        ...state.subKits[subKitIndex].kitProducts[labourItemIndex],
        ...data,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[labourItemIndex] = item;
      });
    }

    case ADD_LABOUR_ITEM_TO_SUBKIT: {
      const { subKitIndex, data } = action.payload;

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts = data.map((d) => ({
          ...d,
          cartIndex: state.subKits[subKitIndex].kitProducts.length,
        }));
      });
    }

    case ADD_NOTE: {
      const data = action.payload;

      const newNoteItem = {
        product: null,
        productRow: null,
        productType: PRODUCT_TYPE_NOTES_ITEM_LABEL,
        quantity: null,
        actualQuantity: null,
        isCompleted: false,
        lengthItems: [],
        actualLengthItems: [],
        sumLengths: [],
        actualSumLengths: [],
        discount: 0.0,
        customValue: null,
        totalValue: 0.0,
        types: [],
        productName: "",
        customFormula: null,
        errorInCustomFormula: "",
        uid: null,
        customAttributeValues: [],
        customRowPrices: [],
        cartIndex: state.kitProducts.length,
        notesType: data.notesType,
        notes: data.notes || "",
      };

      return {
        ...state,
        kitProducts: [...state.kitProducts, newNoteItem],
      };
    }

    case UPDATE_NOTE: {
      const { noteIndex, notes } = action.payload;
      const newNoteItem = {
        ...state.kitProducts[noteIndex],
        notes,
      };

      return produce(state, (draftState) => {
        draftState.kitProducts[noteIndex] = newNoteItem;
      });
    }

    case UPDATE_NOTE_IN_SUBKIT: {
      const { subKitIndex, noteIndex, notes } = action.payload;

      const newNoteItem = {
        ...state.subKits[subKitIndex].kitProducts[noteIndex],
        notes,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[noteIndex] = newNoteItem;
      });
    }

    case ADD_NOTE_TO_SUBKIT: {
      const { subKitIndex, notesType } = action.payload;

      const newNoteItem = {
        product: null,
        productRow: null,
        productType: PRODUCT_TYPE_NOTES_ITEM_LABEL,
        quantity: null,
        actualQuantity: null,
        isCompleted: false,
        lengthItems: [],
        actualLengthItems: [],
        sumLengths: [],
        actualSumLengths: [],
        discount: 0.0,
        customValue: null,
        totalValue: 0.0,
        types: [],
        productName: "",
        customFormula: null,
        errorInCustomFormula: "",
        uid: null,
        customAttributeValues: [],
        customRowPrices: [],
        cartIndex: state.subKits[subKitIndex].kitProducts.length,
        notesType: notesType,
        notes: "",
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts = [
          ...state.subKits[subKitIndex].kitProducts,
          newNoteItem,
        ];
      });
    }

    case ADD_PRODUCT_TO_SUB_KIT_CART: {
      const { subKitIndex, product, productRow } = action.payload;
      const newProduct = {
        ...product,
        suppliers: [],
      };
      const newProductRow = {
        ...productRow.original,
      };
      const newCustomFormula = {
        ...product.customFormula,
        uid: null,
        kitProductUid: null,
        additionalProductUid: null,
      };
      const customRowPrices = newProductRow.rowPrices.map((rowPrice) => ({
        ...rowPrice,
        uid: null,
        value: 0,
      }));
      const newCartItem = {
        product: newProduct,
        productRow: newProductRow,
        productType: PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
        quantity: product.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL ? null : 1.0,
        actualQuantity:
          product.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL ? null : 1.0,
        isCompleted: false,
        lengthItems: [],
        actualLengthItems: [],
        sumLengths: [],
        actualSumLengths: [],
        discount: 0.0,
        customValue: null,
        totalValue: 0.0,
        types: newProductRow.types,
        productName: newProduct.name,
        customFormula:
          product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL
            ? newCustomFormula
            : null,
        errorInCustomFormula: "",
        uid: null,
        customAttributeValues: [],
        customRowPrices,
        cartIndex: state.subKits[subKitIndex].kitProducts.length,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts = [
          ...state.subKits[subKitIndex].kitProducts,
          newCartItem,
        ];
      });
    }

    case ADD_EMPTY_SUB_KIT: {
      const newSubKit = {
        title: "",
        kitProducts: [],
        productKitProducts: {},
      };
      const newSubKits = [...state.subKits, newSubKit];

      return produce(state, (draftState) => {
        draftState.subKits = newSubKits;
      });
    }

    case ADD_SUB_KIT_FROM_KIT: {
      const { productKitItem } = action.payload;
      const { subKits } = productKitItem;

      const newSubKitsFromKit = subKits.map((subKit) => {
        const kitProducts = subKit.kitProducts.map((cartItem, cartIndex) => {
          const { product, productRow } = cartItem;

          if (cartItem.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
            const newProduct = {
              ...product,
              suppliers: [],
            };
            const newCustomFormula = {
              ...newProduct.customFormula,
              uid: null,
              kitProductUid: null,
              additionalProductUid: null,
            };
            const customRowPrices = productRow.rowPrices.map((rowPrice) => ({
              ...rowPrice,
              uid: null,
            }));
            return {
              productType: PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
              product: newProduct,
              productRow,
              quantity: cartItem.quantity,
              actualQuantity: cartItem.actualQuantity,
              isCompleted: false,
              lengthItems: cartItem.lengthItems,
              actualLengthItems: [],
              sumLengths: cartItem.sumLengths,
              actualSumLengths: [],
              discount: 0.0,
              customValue: null,
              totalValue: 0.0,
              types: productRow.types,
              productName: newProduct.name,
              customFormula:
                newProduct.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL
                  ? newCustomFormula
                  : null,
              errorInCustomFormula: "",
              uid: null,
              customAttributeValues: [],
              customRowPrices,
              cartIndex,
            };
          }
          if (cartItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
            return {
              ...cartItem,
              ...cartItem.labour,
              uid: null,
              cartIndex,
            };
          }
          if (cartItem.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
            return {
              ...cartItem,
              uid: null,
              cartIndex,
            };
          }

          return [];
        }, []);

        return {
          ...subKit,
          uid: null,
          kitProducts,
        };
      });

      const kitProducts = productKitItem.kitProducts.map((cartItem, cartIndex) => {
        const { product, productRow } = cartItem;

        if (cartItem.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
          const newProduct = {
            ...product,
            suppliers: [],
          };

          const newCustomFormula = {
            ...newProduct.customFormula,
            uid: null,
            kitProductUid: null,
            additionalProductUid: null,
          };
          const customRowPrices = productRow.rowPrices.map((rowPrice) => ({
            ...rowPrice,
            uid: null,
          }));

          return {
            productType: cartItem.productType,
            product: newProduct,
            productRow,
            quantity: cartItem.quantity,
            actualQuantity: cartItem.actualQuantity,
            isCompleted: false,
            lengthItems: cartItem.lengthItems,
            actualLengthItems: [],
            sumLengths: cartItem.sumLengths,
            actualSumLengths: [],
            discount: 0.0,
            customValue: null,
            totalValue: 0.0,
            types: productRow.types,
            productName: newProduct.name,
            customFormula:
              newProduct.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL
                ? newCustomFormula
                : null,
            errorInCustomFormula: "",
            uid: null,
            customAttributeValues: [],
            customRowPrices,
            cartIndex,
          };
        }
        if (cartItem.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          return {
            ...cartItem,
            ...cartItem.labour,
            uid: null,
            cartIndex,
          };
        }
        if (cartItem.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
          return {
            ...cartItem,
            uid: null,
            cartIndex,
          };
        }

        return [];
      }, []);

      const newSubKit = {
        ...productKitItem,
        uid: null,
        title: productKitItem.title,
        kitProducts,
      };

      const newSubKits = [...state.subKits, newSubKit, ...newSubKitsFromKit];

      return produce(state, (draftState) => {
        draftState.subKits = newSubKits;
      });
    }

    case UPDATE_CART_QUANTITY: {
      const { cartIndex, newQuantity } = action.payload;
      const newCartItem = {
        ...state.kitProducts[cartIndex],
        quantity: newQuantity,
        totalValue: state.kitProducts[cartIndex].value * newQuantity,
      };

      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_SUB_KIT_CART_QUANTITY: {
      const { subKitIndex, cartIndex, newQuantity } = action.payload;

      const newCartItem = {
        ...state.subKits[subKitIndex].kitProducts[cartIndex],
        quantity: newQuantity,
        totalValue: state.subKits[subKitIndex].kitProducts[cartIndex].value * newQuantity,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_CART_COLOUR: {
      const { cartIndex, newColour } = action.payload;
      const newProductRow = {
        ...state.kitProducts[cartIndex].productRow,
        colour: newColour,
      };
      const newCartItem = {
        ...state.kitProducts[cartIndex],
        productRow: newProductRow,
      };
      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_SUB_KIT_CART_COLOUR: {
      const { subKitIndex, cartIndex, newColour } = action.payload;
      const newProductRow = {
        ...state.subKits[subKitIndex].kitProducts[cartIndex].productRow,
        colour: newColour,
      };
      const newCartItem = {
        ...state.subKits[subKitIndex].kitProducts[cartIndex],
        productRow: newProductRow,
      };
      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_CART_PRICE: {
      const { cartIndex, newPrice, rowPriceUid } = action.payload;
      const newCartItem = {
        ...state.kitProducts[cartIndex],
        rowPriceUid,
        value: newPrice,
        totalValue: newPrice * state.kitProducts[cartIndex].quantity,
      };
      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_SUB_KIT_CART_PRICE: {
      const { subKitIndex, cartIndex, newPrice, rowPriceUid } = action.payload;
      const newCartItem = {
        ...state.subKits[subKitIndex].kitProducts[cartIndex],
        rowPriceUid,
        value: newPrice,
        totalValue: newPrice * state.subKits[subKitIndex].kitProducts[cartIndex].quantity,
      };
      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex] = newCartItem;
      });
    }

    case UPDATE_CART: {
      const { cartIndex, path, value } = action.payload;

      return produce(state, (draft) => {
        setKeypathValue(draft, ["kitProducts", [cartIndex], ...path], value);
      });
    }

    case UPDATE_SUB_KIT_CART: {
      const { subKitIndex, cartIndex, path, value } = action.payload;

      return produce(state, (draft) => {
        setKeypathValue(
          draft,
          ["subKits", [subKitIndex], "kitProducts", [cartIndex], ...path],
          value
        );
      });
    }

    case UPDATE_CART_ATTRIBUTES: {
      const { cartIndex, newAttribute } = action.payload;

      return produce(state, (draftState) => {
        const existingCustomAttributeValues =
          draftState.kitProducts[cartIndex].customAttributeValues || [];

        const updatedCustomAttributeValues = existingCustomAttributeValues.map(
          (existingAttribute) => {
            if (existingAttribute.name === newAttribute.name) {
              return {
                ...existingAttribute,
                value: newAttribute.value,
              };
            }

            return existingAttribute;
          }
        );

        if (!existingCustomAttributeValues.some((attr) => attr.name === newAttribute.name)) {
          updatedCustomAttributeValues.push(newAttribute);
        }

        draftState.kitProducts[cartIndex].customAttributeValues = updatedCustomAttributeValues;
      });
    }

    case UPDATE_SUB_KIT_CART_ATTRIBUTES: {
      const { subKitIndex, cartIndex, newAttribute } = action.payload;

      return produce(state, (draftState) => {
        const existingCustomAttributeValues =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].customAttributeValues || [];

        const updatedCustomAttributeValues = existingCustomAttributeValues.map(
          (existingAttribute) => {
            if (existingAttribute.name === newAttribute.name) {
              return {
                ...existingAttribute,
                value: newAttribute.value,
              };
            }

            return existingAttribute;
          }
        );

        if (!existingCustomAttributeValues.some((attr) => attr.name === newAttribute.name)) {
          updatedCustomAttributeValues.push(newAttribute);
        }

        draftState.subKits[subKitIndex].kitProducts[cartIndex].customAttributeValues =
          updatedCustomAttributeValues;
      });
    }

    case DELETE_CART_ATTRIBUTES: {
      const { cartIndex, currentAttributeName } = action.payload;

      return produce(state, (draftState) => {
        const existingCustomAttributeValues =
          draftState.kitProducts[cartIndex].customAttributeValues || [];

        const updatedCustomAttributeValues = existingCustomAttributeValues.filter(
          (existingAttribute) => existingAttribute.name !== currentAttributeName
        );

        draftState.kitProducts[cartIndex].customAttributeValues = updatedCustomAttributeValues;
      });
    }

    case DELETE_SUB_KIT_CART_ATTRIBUTES: {
      const { subKitIndex, cartIndex, currentAttributeName } = action.payload;

      return produce(state, (draftState) => {
        const existingCustomAttributeValues =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].customAttributeValues || [];

        const updatedCustomAttributeValues = existingCustomAttributeValues.filter(
          (existingAttribute) => existingAttribute.name !== currentAttributeName
        );

        draftState.subKits[subKitIndex].kitProducts[cartIndex].customAttributeValues =
          updatedCustomAttributeValues;
      });
    }

    case DELETE_CART_ITEM: {
      const remainingKitProducts = state.kitProducts
        .filter((_cartItem, cartIndex) => cartIndex !== action.payload)
        .map((kitProduct, cartIndex) => ({ ...kitProduct, cartIndex }));

      return {
        ...state,
        kitProducts: remainingKitProducts,
        productKitProducts: buildProductKitProducts(remainingKitProducts),
      };
    }

    case DELETE_SUB_KIT_CART_ITEM: {
      const { subKitIndex, cartIndex } = action.payload;

      return produce(state, (draftState) => {
        const remainingKitProducts = state.subKits[subKitIndex].kitProducts
          .filter((_, kitProductIndex) => kitProductIndex !== cartIndex)
          .map((kitProduct, idx) => ({ ...kitProduct, cartIndex: idx }));

        draftState.subKits[subKitIndex] = {
          ...state.subKits[subKitIndex],
          kitProducts: remainingKitProducts,
          productKitProducts: buildProductKitProducts(remainingKitProducts),
        };
      });
    }

    case ADD_KIT_LENGTH: {
      const { cartIndex, options, minLength, sortLengths } = action.payload;

      if (sortLengths === "largest_to_smallest") {
        return produce(state, (draftState) => {
          const existingLengths = draftState.kitProducts[cartIndex].lengthItems || [];
          const items = [...existingLengths, options];
          const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });
          const orderedLengths = items.sort((a, b) => parseFloat(b.length) - parseFloat(a.length));
          draftState.kitProducts[cartIndex].sumLengths = {
            total: parseFloat(lengths.total),
            forPrice: parseFloat(lengths.forPrice),
          };
          draftState.kitProducts[cartIndex].lengthItems = orderedLengths;
        });
      }
      if (sortLengths === "smallest_to_largest") {
        return produce(state, (draftState) => {
          const existingLengths = draftState.kitProducts[cartIndex].lengthItems || [];
          const items = [...existingLengths, options];
          const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });
          const orderedLengths = items.sort((a, b) => parseFloat(a.length) - parseFloat(b.length));

          draftState.kitProducts[cartIndex].sumLengths = {
            total: parseFloat(lengths.total),
            forPrice: parseFloat(lengths.forPrice),
          };
          draftState.kitProducts[cartIndex].lengthItems = orderedLengths;
        });
      }

      return produce(state, (draftState) => {
        const existingLengths = draftState.kitProducts[cartIndex].lengthItems || [];
        const items = [...existingLengths, options];
        const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });

        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(lengths.total),
          forPrice: parseFloat(lengths.forPrice),
        };
        draftState.kitProducts[cartIndex].lengthItems = items;
      });
    }

    case DELETE_KIT_LENGTH: {
      const { cartIndex, subitemId, minLength } = action.payload;

      return produce(state, (draftState) => {
        // Filter out the specified lengthItem
        draftState.kitProducts[cartIndex].lengthItems = draftState.kitProducts[
          cartIndex
        ].lengthItems.filter((_length, lengthIndex) => lengthIndex !== subitemId);

        const existingLengths = draftState.kitProducts[cartIndex].lengthItems || [];
        const lengths = getLinealMetresQuantityInMm({
          items: existingLengths,
          minLengthInMm: minLength,
        });
        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(lengths.total),
          forPrice: parseFloat(lengths.forPrice),
        };
      });
    }

    // Square Meters
    case ADD_KIT_DIMENSION: {
      const { cartIndex, options } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const totalSqm = getSquareMetresQuantityInSquareMetres({ items });

        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm.total),
        };
        draftState.kitProducts[cartIndex].lengthItems = items;
      });
    }

    case DELETE_KIT_DIMENSION: {
      const { cartIndex, subitemId } = action.payload;

      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex].lengthItems = draftState.kitProducts[
          cartIndex
        ].lengthItems.filter((_length, lengthIndex) => lengthIndex !== subitemId);

        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        const totalSqm = getSquareMetresQuantityInSquareMetres({ items: existingDimensions });
        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm.total),
        };
      });
    }

    case ADD_KIT_LINEAL_FEET_DIMENSION: {
      const { cartIndex, options, minLength, sortLengths } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const totalFt = getLinealFeetQuantityInMm({ items, minLengthInMm: minLength });

        // Sort items based on sortLengths parameter
        let sortedItems = items;
        if (sortLengths === "largest_to_smallest") {
          sortedItems = items.sort((a, b) => parseFloat(b.length) - parseFloat(a.length));
        } else if (sortLengths === "smallest_to_largest") {
          sortedItems = items.sort((a, b) => parseFloat(a.length) - parseFloat(b.length));
        }

        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalFt.total),
          forPrice: parseFloat(totalFt.forPrice),
        };
        draftState.kitProducts[cartIndex].lengthItems = sortedItems;
      });
    }

    case DELETE_KIT_LINEAL_FEET_DIMENSION: {
      const { cartIndex, subitemId, minLength } = action.payload;

      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex].lengthItems = draftState.kitProducts[
          cartIndex
        ].lengthItems.filter((_, lengthIndex) => lengthIndex !== subitemId);

        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        // minLength is 1 meter (equivalent to 1000mm)
        const totalFt = getLinealFeetQuantityInMm({
          items: existingDimensions,
          minLengthInMm: minLength,
        });
        draftState.kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalFt.total),
          forPrice: parseFloat(totalFt.forPrice),
        };
      });
    }

    case ADD_KIT_SQUARE_FEET_DIMENSION: {
      const { cartIndex, options } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const { total: totalSqm } = getSquareFeetQuantityInSquareMeter({ items });
        draftState.kitProducts[cartIndex].sumLengths = { total: parseFloat(totalSqm) };
        draftState.kitProducts[cartIndex].lengthItems = items;
      });
    }

    case DELETE_KIT_SQUARE_FEET_DIMENSION: {
      const { cartIndex, subitemId } = action.payload;

      return produce(state, (draftState) => {
        draftState.kitProducts[cartIndex].lengthItems = draftState.kitProducts[
          cartIndex
        ].lengthItems.filter((_, lengthIndex) => lengthIndex !== subitemId);

        const existingDimensions = draftState.kitProducts[cartIndex].lengthItems || [];
        const { total: totalSqm } = getSquareFeetQuantityInSquareMeter({
          items: existingDimensions,
        });
        draftState.kitProducts[cartIndex].sumLengths = { total: parseFloat(totalSqm) };
      });
    }

    case ADD_SUB_KIT_LENGTH: {
      const { subKitIndex, cartIndex, options, minLength, sortLengths } = action.payload;

      if (sortLengths === "largest_to_smallest") {
        return produce(state, (draftState) => {
          const existingLengths =
            draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
          const items = [...existingLengths, options];
          const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });
          const orderedLengths = items.sort((a, b) => parseFloat(b.length) - parseFloat(a.length));

          draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
            total: parseFloat(lengths.total),
            forPrice: parseFloat(lengths.forPrice),
          };
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = orderedLengths;
        });
      }
      if (sortLengths === "smallest_to_largest") {
        return produce(state, (draftState) => {
          const existingLengths =
            draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
          const items = [...existingLengths, options];
          const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });
          const orderedLengths = items.sort((a, b) => parseFloat(a.length) - parseFloat(b.length));

          draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
            total: parseFloat(lengths.total),
            forPrice: parseFloat(lengths.forPrice),
          };
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = orderedLengths;
        });
      }

      return produce(state, (draftState) => {
        const existingLengths =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const items = [...existingLengths, options];
        const lengths = getLinealMetresQuantityInMm({ items, minLengthInMm: minLength });

        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(lengths.total),
          forPrice: parseFloat(lengths.forPrice),
        };
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = items;
      });
    }

    case DELETE_SUB_KIT_LENGTH: {
      const { subKitIndex, cartIndex, subitemId, minLength } = action.payload;

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = draftState.subKits[
          subKitIndex
        ].kitProducts[cartIndex].lengthItems.filter(
          (_length, lengthIndex) => lengthIndex !== subitemId
        );

        const existingLengths =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const lengths = getLinealMetresQuantityInMm({
          items: existingLengths,
          minLengthInMm: minLength,
        });
        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(lengths.total),
          forPrice: parseFloat(lengths.forPrice),
        };
      });
    }

    // Square Meters
    case ADD_SUB_KIT_DIMENSION: {
      const { subKitIndex, cartIndex, options } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const totalSqm = getSquareMetresQuantityInSquareMetres({ items });

        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm.total),
        };
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = items;
      });
    }

    // Square Meters
    case DELETE_SUB_KIT_DIMENSION: {
      const { subKitIndex, cartIndex, subitemId } = action.payload;

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = draftState.subKits[
          subKitIndex
        ].kitProducts[cartIndex].lengthItems.filter(
          (_length, lengthIndex) => lengthIndex !== subitemId
        );

        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const totalSqm = getSquareMetresQuantityInSquareMetres({ items: existingDimensions });
        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm.total),
        };
      });
    }

    case ADD_SUB_KIT_LINEAL_FEET_DIMENSION: {
      const { subKitIndex, cartIndex, options, minLength, sortLengths } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const totalFt = getLinealFeetQuantityInMm({ items, minLengthInMm: minLength });

        // Sort items based on sortLengths parameter
        let sortedItems = items;
        if (sortLengths === "largest_to_smallest") {
          sortedItems = items.sort((a, b) => parseFloat(b.length) - parseFloat(a.length));
        } else if (sortLengths === "smallest_to_largest") {
          sortedItems = items.sort((a, b) => parseFloat(a.length) - parseFloat(b.length));
        }

        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalFt.total),
          forPrice: parseFloat(totalFt.forPrice),
        };
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = sortedItems;
      });
    }

    case DELETE_SUB_KIT_LINEAL_FEET_DIMENSION: {
      const { subKitIndex, cartIndex, subitemId, minLength } = action.payload;

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = draftState.subKits[
          subKitIndex
        ].kitProducts[cartIndex].lengthItems.filter((_, lengthIndex) => lengthIndex !== subitemId);

        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const totalFt = getLinealFeetQuantityInMm({
          items: existingDimensions,
          minLengthInMm: minLength,
        });
        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalFt.total),
          forPrice: parseFloat(totalFt.forPrice),
        };
      });
    }

    case ADD_SUB_KIT_SQUARE_FEET_DIMENSION: {
      const { subKitIndex, cartIndex, options } = action.payload;

      return produce(state, (draftState) => {
        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const items = [...existingDimensions, options];
        const { total: totalSqm } = getSquareMetresQuantityInSquareMetres({ items });

        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm),
        };
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = items;
      });
    }

    case DELETE_SUB_KIT_SQUARE_FEET_DIMENSION: {
      const { subKitIndex, cartIndex, subitemId } = action.payload;

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems = draftState.subKits[
          subKitIndex
        ].kitProducts[cartIndex].lengthItems.filter((_, lengthIndex) => lengthIndex !== subitemId);

        const existingDimensions =
          draftState.subKits[subKitIndex].kitProducts[cartIndex].lengthItems || [];
        const { total: totalSqm } = getSquareMetresQuantityInSquareMetres({
          items: existingDimensions,
        });
        draftState.subKits[subKitIndex].kitProducts[cartIndex].sumLengths = {
          total: parseFloat(totalSqm),
        };
      });
    }

    case DELETE_SUB_KIT: {
      const { subKitIndex } = action.payload;
      return produce(state, (draftState) => {
        draftState.subKits = draftState.subKits.filter(
          (_subKit, sKitIndex) => sKitIndex !== subKitIndex
        );
      });
    }

    case REORDER_KIT_PRODUCTS: {
      const { startIndex, endIndex } = action.payload;

      const groupKeys = [];
      const groupMap = {};

      state.kitProducts.forEach((item) => {
        let key;
        if (item.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
          key = item.product.uid;
        } else if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
          key = "Labour";
        } else if (item.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
          key = "Notes";
        } else {
          key = `other-${item.cartIndex}`;
        }

        if (!groupMap[key]) {
          groupMap[key] = [];
          groupKeys.push(key);
        }
        groupMap[key].push(item);
      });

      const [movedKey] = groupKeys.splice(startIndex, 1);
      groupKeys.splice(endIndex, 0, movedKey);

      const reorderedKitProducts = groupKeys
        .flatMap((key) => groupMap[key])
        .map((item, index) => ({ ...item, cartIndex: index }));

      return {
        ...state,
        kitProducts: reorderedKitProducts,
      };
    }

    case REORDER_SUB_KIT_PRODUCTS: {
      const { subKitIndex, startIndex, endIndex } = action.payload;

      return produce(state, (draftState) => {
        const subKitProducts = state.subKits[subKitIndex].kitProducts;

        const groupKeys = [];
        const groupMap = {};

        subKitProducts.forEach((item) => {
          let key;
          if (item.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
            key = item.product.uid;
          } else if (item.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
            key = "Labour";
          } else if (item.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
            key = "Notes";
          } else {
            key = `other-${item.cartIndex}`;
          }

          if (!groupMap[key]) {
            groupMap[key] = [];
            groupKeys.push(key);
          }
          groupMap[key].push(item);
        });

        const [movedKey] = groupKeys.splice(startIndex, 1);
        groupKeys.splice(endIndex, 0, movedKey);

        draftState.subKits[subKitIndex].kitProducts = groupKeys
          .flatMap((key) => groupMap[key])
          .map((item, index) => ({ ...item, cartIndex: index }));
      });
    }

    case REORDER_SUB_KITS: {
      const { startIndex, endIndex } = action.payload;
      const result = Array.from(state.subKits);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      // Remap expandedSubKitProducts keys to follow their sub-kits
      const newExpandedSubKitProducts = {};
      for (const [key, value] of Object.entries(state.expandedSubKitProducts)) {
        const idx = Number(key);
        let newIdx;
        if (idx === startIndex) {
          newIdx = endIndex;
        } else if (startIndex < endIndex && idx > startIndex && idx <= endIndex) {
          newIdx = idx - 1;
        } else if (startIndex > endIndex && idx >= endIndex && idx < startIndex) {
          newIdx = idx + 1;
        } else {
          newIdx = idx;
        }
        newExpandedSubKitProducts[newIdx] = value;
      }

      // Update subKitIndex to follow the reordered position
      let newSubKitIndex = state.subKitIndex;
      if (newSubKitIndex !== null) {
        if (newSubKitIndex === startIndex) {
          newSubKitIndex = endIndex;
        } else if (
          startIndex < endIndex &&
          newSubKitIndex > startIndex &&
          newSubKitIndex <= endIndex
        ) {
          newSubKitIndex = newSubKitIndex - 1;
        } else if (
          startIndex > endIndex &&
          newSubKitIndex >= endIndex &&
          newSubKitIndex < startIndex
        ) {
          newSubKitIndex = newSubKitIndex + 1;
        }
      }

      return {
        ...state,
        subKits: result,
        expandedSubKitProducts: newExpandedSubKitProducts,
        subKitIndex: newSubKitIndex,
      };
    }

    case REORDER_KIT_PRODUCT_ROWS: {
      const { cartIndices, startIndex, endIndex } = action.payload;
      const newKitProducts = [...state.kitProducts];
      const items = cartIndices.map((idx) => newKitProducts[idx]);
      const [moved] = items.splice(startIndex, 1);
      items.splice(endIndex, 0, moved);
      cartIndices.forEach((cartIdx, i) => {
        newKitProducts[cartIdx] = items[i];
      });

      return {
        ...state,
        kitProducts: newKitProducts.map((item, index) => ({ ...item, cartIndex: index })),
      };
    }

    case REORDER_SUB_KIT_PRODUCT_ROWS: {
      const { subKitIndex, cartIndices, startIndex, endIndex } = action.payload;

      return produce(state, (draftState) => {
        const newKitProducts = [...state.subKits[subKitIndex].kitProducts];
        const items = cartIndices.map((idx) => newKitProducts[idx]);
        const [moved] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, moved);
        cartIndices.forEach((cartIdx, i) => {
          newKitProducts[cartIdx] = items[i];
        });
        draftState.subKits[subKitIndex].kitProducts = newKitProducts.map((item, index) => ({
          ...item,
          cartIndex: index,
        }));
      });
    }

    case CREATE_PRODUCT_KIT_FROM_CART: {
      return produce(state, (draftState) => {
        const subKits = state.subKits.map((subKit) => {
          const reindexedKitProducts = subKit.kitProducts.map((kitProduct, cartIndex) => ({
            ...kitProduct,
            cartIndex,
          }));

          return {
            ...subKit,
            productKitProducts: buildProductKitProducts(reindexedKitProducts),
            kitProducts: reindexedKitProducts,
          };
        });

        const reindexedKitProducts = state.kitProducts.map((kitProduct, cartIndex) => ({
          ...kitProduct,
          cartIndex,
        }));

        draftState.productKitProducts = buildProductKitProducts(reindexedKitProducts);
        draftState.subKits = subKits;
        draftState.kitProducts = reindexedKitProducts;
      });
    }

    case SET_TARGET_SUB_KIT_INDEX: {
      return {
        ...state,
        subKitIndex: action.payload,
      };
    }

    case SET_IS_SUB_KIT_MODAL_OPEN: {
      const { isSubKitModalOpen, subKitIndex } = action.payload;

      return {
        ...state,
        isSubKitModalOpen,
        subKitIndex,
      };
    }

    case CREATE_SUB_KIT_FROM_LABOUR_LINE_ITEMS_CART: {
      return produce(state, (draftState) => {
        draftState.subKits[draftState.subKitIndex].productKitProducts.Labour = {
          uid: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
          name: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
          productType: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
          productRows: state.subKits[state.subKitIndex].kitProducts.filter(
            (p) => p.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL
          ),
        };
      });
    }

    case CREATE_PRODUCT_KIT_FROM_LABOUR_LINE_ITEMS_CART: {
      return {
        ...state,
        productKitProducts: {
          ...state.productKitProducts,
          Labour: {
            uid: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
            name: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
            productType: PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
            productRows: state.kitProducts.filter(
              (p) => p.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL
            ),
          },
        },
      };
    }

    case TOGGLE_PRODUCT_KIT_MODAL: {
      return {
        ...state,
        isModalVisible: !state.isModalVisible,
      };
    }

    case TOGGLE_CART: {
      return {
        ...state,
        isCartVisible: action.payload,
      };
    }

    case UPDATE_CURRENT_PRODUCT_KIT: {
      const { path, value } = action.payload;
      const newCurrentProductKit = {
        ...state.currentProductKit,
        [path]: value,
      };

      return {
        ...state,
        currentProductKit: newCurrentProductKit,
      };
    }

    case UPDATE_SUB_KIT_TITLE: {
      const { subKitIndex, title } = action.payload;

      const newSubKit = {
        ...state.subKits[subKitIndex],
        title,
      };

      return produce(state, (draftState) => {
        draftState.subKits[subKitIndex] = newSubKit;
      });
    }

    case UPDATE_PRODUCT_KIT_PRICING_TABLE: {
      const { path, value } = action.payload;

      return produce(state, (draft) => {
        setKeypathValue(draft, ["currentProductKit", ...path], value);
      });
    }

    case SET_PRODUCT_KIT: {
      const { data } = action.payload;
      const { productKitItem, image, customerPriceLevel } = data;
      const allPriceLevelsHidden =
        productKitItem.priceLevels.length === productKitItem.hiddenPriceLevels.length;

      const priceLevels = allPriceLevelsHidden
        ? productKitItem.priceLevels.map((priceLevel, index) =>
            index === 0 ? { ...priceLevel, isHidden: false } : priceLevel
          )
        : productKitItem.priceLevels;

      const hiddenPriceLevels = allPriceLevelsHidden
        ? productKitItem.hiddenPriceLevels.filter(
            (priceLevelUid) => priceLevelUid !== productKitItem.priceLevels[0].uid
          )
        : productKitItem.hiddenPriceLevels;

      const currentProductKit = {
        ...data,
        title: productKitItem.title,
        description: productKitItem.description,
        customPricing: productKitItem.customPricing,
        displayKitItems: productKitItem.displayKitItems,
        displaySubKits: productKitItem.displaySubKits,
        productKitRows: productKitItem.productKitRows,
        fields: productKitItem.fields,
        accountingItem: productKitItem.accountingItem,
        category: data.category,
        categoryUid: data.categoryUid,
        oldCategoryUid: data.categoryUid,
        image,
        priceLevels,
        hiddenPriceLevels,
      };

      if (!currentProductKit.customPricing) {
        const newKitProducts = productKitItem.kitProducts.map((kitProduct) => {
          if (kitProduct.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
            const currentTable = kitProduct.product.tables.find(
              (table) => table.name === kitProduct.productRow.material
            );
            const notHiddenPrices = currentTable.priceLevels.filter(
              (priceLevel) => !priceLevel.isHidden
            );
            const firstPrice = kitProduct.productRow.rowPrices.find(
              (rowPrice) => rowPrice.priceLevelUid === notHiddenPrices[0].uid
            );
            const firstCustomPrice = kitProduct.customRowPrices.find(
              (customRowPrice) => customRowPrice.priceLevelUid === notHiddenPrices[0].uid
            );

            if (customerPriceLevel) {
              const customerPriceLevelIsHidden = !notHiddenPrices.some(
                (priceLevel) => priceLevel.uid === customerPriceLevel.uid
              );

              const price = customerPriceLevelIsHidden
                ? firstPrice
                : kitProduct.productRow.rowPrices.find(
                    (p) => p.priceLevelUid === customerPriceLevel?.uid
                  );

              const customPrice = customerPriceLevelIsHidden
                ? firstCustomPrice
                : kitProduct.customRowPrices.find(
                    (customRowPrice) => customRowPrice.priceLevelUid === customerPriceLevel?.uid
                  );

              const value = customPrice.value !== 0 ? customPrice.value : price.value;

              return {
                ...kitProduct,
                value,
                totalValue: kitProduct.quantity * value,
                rowPriceUid: price.uid,
              };
            }

            const customPrice = kitProduct.customRowPrices.find(
              (customRowPrice) => customRowPrice.priceLevelUid === notHiddenPrices[0].uid
            );

            const value = customPrice.value !== 0 ? customPrice.value : firstPrice.value;

            return {
              ...kitProduct,
              value,
              totalValue: kitProduct.quantity * value,
              rowPriceUid: firstPrice.uid,
            };
          }

          if (kitProduct.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
            return {
              ...kitProduct,
              notes: kitProduct.notes,
              notesType: kitProduct.notesType,
            };
          }

          if (kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
            return {
              ...kitProduct,
              ...kitProduct.labour,
            };
          }

          return [];
        });

        const newSubKits = productKitItem.subKits.map((subKit) => {
          const newSubKitProducts = subKit.kitProducts.map((kitProduct, cartIndex) => {
            if (kitProduct.productType === PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL) {
              const currentTable = kitProduct.product.tables.find(
                (table) => table.name === kitProduct.productRow.material
              );
              const notHiddenPrices = currentTable.priceLevels.filter(
                (priceLevel) => !priceLevel.isHidden
              );
              const firstPrice = kitProduct.productRow.rowPrices.find(
                (rowPrice) => rowPrice.priceLevelUid === notHiddenPrices[0].uid
              );
              const firstCustomPrice = kitProduct.customRowPrices.find(
                (customRowPrice) => customRowPrice.priceLevelUid === notHiddenPrices[0].uid
              );

              if (customerPriceLevel) {
                const customerPriceLevelIsHidden = !notHiddenPrices.some(
                  (priceLevel) => priceLevel.uid === customerPriceLevel.uid
                );
                const price = customerPriceLevelIsHidden
                  ? firstPrice
                  : kitProduct.productRow.rowPrices.find(
                      (p) => p.priceLevelUid === customerPriceLevel?.uid
                    );
                const customPrice = customerPriceLevelIsHidden
                  ? firstCustomPrice
                  : kitProduct.customRowPrices.find(
                      (customRowPrice) => customRowPrice.priceLevelUid === customerPriceLevel?.uid
                    );
                const value = customPrice.value !== 0 ? customPrice.value : price.value;

                return {
                  ...kitProduct,
                  value,
                  totalValue: kitProduct.quantity * value,
                  rowPriceUid: price.uid,
                  pricingStrategy: kitProduct.pricingStrategy,
                  cartIndex,
                };
              }

              const customPrice = kitProduct.customRowPrices.find(
                (customRowPrice) => customRowPrice.priceLevelUid === notHiddenPrices[0].uid
              );
              const value = customPrice.value !== 0 ? customPrice.value : firstPrice.value;

              return {
                ...kitProduct,
                value,
                totalValue: kitProduct.quantity * value,
                rowPriceUid: firstPrice.uid,
                pricingStrategy: kitProduct.pricingStrategy,
                cartIndex,
              };
            }

            if (kitProduct.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
              return {
                ...kitProduct,
                notes: kitProduct.notes,
                notesType: kitProduct.notesType,
              };
            }

            if (kitProduct.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
              return {
                ...kitProduct,
                ...kitProduct.labour,
              };
            }

            return [];
          });

          return {
            ...subKit,
            kitProducts: newSubKitProducts,
          };
        });

        return {
          ...state,
          currentProductKit,
          kitProducts: newKitProducts,
          subKits: newSubKits,
          selectedPriceLevels: priceLevels
            .filter((price) => !price.isHidden)
            .map((price) => price.uid),
        };
      }

      return {
        ...state,
        selectedAttributeVisiblity: currentProductKit.fields.map((field) => ({
          ...field,
          uid: nanoid(),
        })),
        currentProductKit,
        kitProducts: productKitItem.kitProducts.map((kitProduct, cartIndex) => ({
          ...kitProduct,
          cartIndex,
        })),
        subKits: productKitItem.subKits.map((subKit) => ({
          ...subKit,
          kitProducts: subKit.kitProducts.map((subKitProduct, cartIndex) => ({
            ...subKitProduct,
            cartIndex,
          })),
        })),
        selectedPriceLevels: priceLevels
          .filter((price) => !price.isHidden)
          .map((price) => price.uid),
      };
    }

    case SET_KIT_LABOUR_LINE_ITEMS: {
      return {
        ...state,
        kitLabourLineItems: action.payload,
      };
    }

    case SET_SELECTED_PRICE_LEVELS: {
      const { data } = action.payload;

      return {
        ...state,
        selectedPriceLevels: data,
      };
    }

    // EDIT SELECTED ATTRIBUTE VISIBLITY //
    case EDIT_SELECTED_ATTRIBUTE_VISIBLITY: {
      const { path, value } = action.payload;

      return produce(state, (draft) => {
        setKeypathValue(draft, ["selectedAttributeVisiblity", ...path], value);
      });
    }

    case RESET_PRODUCT_KIT: {
      return {
        ...state,
        productKitProducts: {},
      };
    }

    case RESET_EXPANDED_PRODUCTS: {
      return {
        ...state,
        expandedProducts: [],
      };
    }

    case RESET_EXPANDED_SUB_KIT_PRODUCTS: {
      return {
        ...state,
        expandedSubKitProducts: {},
      };
    }

    case RESET: {
      return { ...initialState };
    }

    default:
      return state;
  }
};
